'use client';

;
!function () {
  try {
    var e = "undefined" != typeof window ? window : "undefined" != typeof global ? global : "undefined" != typeof globalThis ? globalThis : "undefined" != typeof self ? self : {};
    e._sentryModuleMetadata = e._sentryModuleMetadata || {}, e._sentryModuleMetadata[new e.Error().stack] = Object.assign({}, e._sentryModuleMetadata[new e.Error().stack], {
      "_sentryBundlerPluginAppKey:localcoin": true
    });
  } catch (e) {}
}();
import { format } from 'url';
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import clsx from 'clsx';
import { useRouter } from 'next/navigation';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import { useTranslation } from 'react-i18next';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import { BaseAutocompleteItem } from '@/components/atoms/Autocomplete/types';
import Spinner from '@/components/atoms/Spinner';
import { useGoogleMaps } from '@/contexts/GoogleMapsProvider';
import { useLang } from '@/hooks/useLang';
import useUserLocation from '@/hooks/useUserLocation';
import { MapPinIcon } from '@/public/assets/svg';
import { ROUTES } from '@/routes';
import { pushGTMEvent } from '@/utils/gtm';
import { HOME_PAGE_ADDRESS_ENTERED_EVENT } from '@/utils/gtm/consts';
import { checkIfIOS, disableIosTextFieldZoom } from '@/utils/safari';
import { LocationSelectProps, LocationSelectOption, PlacesOption, DefaultLocationOption } from './types';
import { getDefaultLocationsByLocale } from './utils';
const LocationSelect = ({
  placeholder,
  className
}: LocationSelectProps) => {
  const router = useRouter();
  const lang = useLang();
  const {
    t
  } = useTranslation();
  const initRef = useRef(false);
  const buttonTextRef = useRef<HTMLSpanElement>(null);
  const [buttonTextWidth, setButtonTextWidth] = useState<number>(0);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedOption, setSelectedOption] = useState<LocationSelectOption | null>(null);
  const [geocodedLocation, setGeocodedLocation] = useState<LocationSelectOption | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const {
    data: userLocation
  } = useUserLocation();

  // With no user location there is nothing to geocode, so read that as "no current
  // location" during render rather than clearing state from an effect.
  const currentLocation = userLocation ? geocodedLocation : null;
  const {
    isLoaded
  } = useGoogleMaps();
  const {
    ready,
    setValue: setGooglePlacesValue,
    suggestions: {
      data,
      status: googlePlacesStatus
    },
    clearSuggestions,
    init,
    value
  } = usePlacesAutocomplete({
    debounce: 300,
    initOnMount: false
  });
  const ref = useOnclickOutside(() => {
    // When the user clicks outside of the component, we can dismiss
    // the searched suggestions by calling this method
    clearSuggestions();
  });
  const handleSubmit = async () => {
    let selectedLatitude: number, selectedLongitude: number;
    try {
      setIsLoading(true);
      if (!selectedOption) {
        const url = format({
          pathname: ROUTES.bitcoinATMLocalized(lang)
        });
        pushGTMEvent({
          event: HOME_PAGE_ADDRESS_ENTERED_EVENT
        });
        router.push(url);
        return;
      }
      if (selectedOption.type === 'defaultLocation') {
        const [latitude, longitude] = selectedOption.value.split(',').map(Number);
        selectedLatitude = latitude;
        selectedLongitude = longitude;
      } else {
        const placeId = selectedOption.value;
        const geocode = await getGeocode({
          placeId
        });
        const {
          lat: latitude_0,
          lng: longitude_0
        } = getLatLng(geocode[0]);
        selectedLatitude = latitude_0;
        selectedLongitude = longitude_0;
      }
      const url_0 = format({
        pathname: ROUTES.bitcoinATMLocalized(lang),
        query: {
          latitude: selectedLatitude,
          longitude: selectedLongitude,
          placeId: selectedOption.value
        }
      });
      pushGTMEvent({
        event: HOME_PAGE_ADDRESS_ENTERED_EVENT
      });
      router.push(url_0);
    } catch {
      setIsLoading(false);
    }
  };
  const options: LocationSelectOption[] = useMemo(() => {
    if (!ready) {
      return [];
    }
    if (googlePlacesStatus === 'ZERO_RESULTS') {
      return [{
        label: 'No results',
        value: '',
        type: 'defaultLocation'
      } as DefaultLocationOption];
    }
    if (data.length === 0 && currentLocation && !value) {
      if (userLocation?.isFromBrowser) {
        return [currentLocation];
      }
      const defaultLocations = getDefaultLocationsByLocale(lang);
      return defaultLocations.map<DefaultLocationOption>(city => ({
        label: city.city,
        value: `${city.latitude},${city.longitude}`,
        type: 'defaultLocation'
      }));
    }
    return data.map<PlacesOption>(suggestion => {
      const {
        place_id,
        structured_formatting: {
          main_text,
          secondary_text
        }
      } = suggestion;
      return {
        label: [main_text, secondary_text].filter(Boolean).join(' ') || 'Missing label',
        value: place_id,
        type: 'places'
      };
    });
  }, [ready, data, value, googlePlacesStatus, currentLocation, userLocation?.isFromBrowser, lang]);
  useEffect(() => {
    if (!ready || !userLocation) {
      return;
    }
    const setUserLocationAsOption = async () => {
      try {
        const {
          latitude: latitude_1,
          longitude: longitude_1
        } = userLocation?.location || {};
        const geocode_0 = await getGeocode({
          location: {
            lat: latitude_1,
            lng: longitude_1
          }
        });
        const {
          place_id: place_id_0,
          formatted_address
        } = geocode_0[0];
        setGeocodedLocation({
          label: formatted_address,
          value: place_id_0,
          type: 'places'
        });
      } catch {
        setGeocodedLocation(null);
      }
    };
    setUserLocationAsOption();
  }, [ready, userLocation]);
  useEffect(() => {
    if (checkIfIOS()) {
      disableIosTextFieldZoom();
    }
  }, []);
  useEffect(() => {
    if (isLoaded && !initRef.current) {
      init();
      initRef.current = true;
    }
  }, [isLoaded, init]);
  useLayoutEffect(() => {
    if (buttonTextRef.current) {
      setButtonTextWidth(buttonTextRef.current.offsetWidth);
    }
  }, []);
  return <div className={clsx('relative flex w-full flex-col gap-2 md:flex-row', className)} ref={ref} data-sentry-component="LocationSelect" data-sentry-source-file="index.tsx">
      <Combobox<LocationSelectOption | null> value={selectedOption} onChange={option => {
      setSelectedOption(option);
      clearSuggestions();
    }} virtual={{
      options
    }} onClose={() => setIsFocused(false)} immediate data-sentry-element="Combobox" data-sentry-source-file="index.tsx">
        <div className="relative w-full">
          <div className={clsx('relative w-full cursor-default overflow-hidden rounded-md border border-gray-200 bg-white text-left hover:border-gray-300', {
          'rounded-b-none': isFocused
        })}>
            <div className="absolute top-0 bottom-0 left-3.5 flex items-center justify-center">
              <MapPinIcon width={16} height={16} data-sentry-element="MapPinIcon" data-sentry-source-file="index.tsx" />
            </div>
            <ComboboxInput className={clsx('text-button caret-primary-600 w-full border-none py-3.5 pr-12 pl-10 text-gray-600 shadow-none focus:outline-hidden')} placeholder={placeholder} onChange={event => {
            const newValue = event.target.value;
            setGooglePlacesValue(newValue);
          }} displayValue={() => selectedOption?.label || ''} onFocus={() => {
            setIsFocused(true);
            setGooglePlacesValue('');
            setSelectedOption(null);
          }} onBlur={() => {
            setIsFocused(false);
          }} data-sentry-element="ComboboxInput" data-sentry-source-file="index.tsx" />
          </div>
          <ComboboxOptions className="absolute z-50 max-h-60 w-full overflow-auto rounded-md rounded-t-none border border-t-0 border-gray-200 bg-white" modal={false} data-sentry-element="ComboboxOptions" data-sentry-source-file="index.tsx">
            {({
            option: option_0
          }) => <ComboboxOption value={option_0} className={clsx('text-button relative w-full px-3 py-3.5 text-gray-600 select-none data-focus:bg-gray-50', {
            'cursor-pointer hover:bg-gray-50': googlePlacesStatus !== 'ZERO_RESULTS'
          })} disabled={googlePlacesStatus === 'ZERO_RESULTS'}>
                {(option_0 as BaseAutocompleteItem).label}
              </ComboboxOption>}
          </ComboboxOptions>
        </div>
      </Combobox>
      <div className="flex shrink-0 items-center justify-center">
        <button name="select-submit" disabled={isLoading} onClick={handleSubmit} className={clsx('bg-primary-600 flex w-full items-center justify-center rounded-md px-4 py-3.5', {
        'hover:bg-primary-700': !isLoading
      })} aria-label="select-submit">
          {isLoading ? <div style={{
          width: `${buttonTextWidth}px`
        }} className="flex items-center justify-center">
              <Spinner className="h-5 w-5" />
            </div> : <span ref={buttonTextRef} className="text-body-8 font-medium">
              {t('hero.findAtmButton')}
            </span>}
        </button>
      </div>
    </div>;
};
export default LocationSelect;